import appConfig from 'appConfig';
import { ALL_MOTIVATIONS } from './motivations';
import CoursewareTargetProvider from '../coursewareTarget/CoursewareTargetProvider';

export function validateMotivation(motivation) {
  if (!ALL_MOTIVATIONS.includes(motivation)) {
    throw new Error(`Invalid motivation (${motivation}), must be one of ${ALL_MOTIVATIONS.join(', ')}`);
  }
}

export function validateBodyString(bodyString) {
  let parsedBody;
  try {
    parsedBody = JSON.parse(bodyString);
  } catch (error) {
    throw new Error(`Invalid annnotation body - expecting json format, received ${bodyString}`);
  }

  if (!Array.isArray(parsedBody)) {
    throw new Error(`Invalid annnotation body - expecting an array of annotation body objects, received ${bodyString}`);
  }

  parsedBody.forEach((bodyObject) => {
    if (typeof bodyObject !== 'object') {
      throw new Error(`Invalid annnotation body - expecting an array of annotation body objects, received ${bodyString}`);
    }

    if (!ALL_MOTIVATIONS.includes(bodyObject.purpose)) {
      throw new Error(`Invalid annnotation body - expecting a purpose that matches one of; ${ALL_MOTIVATIONS.join(', ')}`);
    }
  });
}

export function createAnnotationBodyString(body = []) {
  if (typeof body === 'string') {
    return body;
  }

  if (Array.isArray(body)) {
    return JSON.stringify(body);
  }

  return JSON.stringify([body]);
}

function toTargetSelectorValue(target) {
  if (target.type === 'INTERACTIVE') {
    // Interactives will have a patway as a direct parent. Skip that for the activity
    return `${target.ancestry[1].elementId}/${target.id}`;
  }

  if (target.type === 'COMPONENT' && target.ancestry[0].elementType === 'ACTIVITY') {
    return `${target.ancestry[0].elementId}/${target.id}`;
  }

  if (target.type === 'COMPONENT' && target.ancestry[0].elementType === 'INTERACTIVE') {
    return `${target.ancestry[2].elementId}/${target.ancestry[0].elementId}/${target.id}`;
  }

  return target.id;
}

export function createAnnotationTarget(target, subTarget) {
  const targetObject = {
    source: `${appConfig.getLearnspaceURL()}/${target.cohortId}/${target.deploymentId}`,
    selector: {
      type: 'FragmentSelector',
      value: toTargetSelectorValue(target),
    },
  };

  if (subTarget) {
    targetObject.selector.refinedBy = {
      type: 'FragmentSelector',
      value: subTarget,
    };
  }

  return targetObject;
}

export function createAnnotationTargetString(target, subTarget) {
  return JSON.stringify([
    createAnnotationTarget(target, subTarget),
  ]);
}

export const sortByModifiedAndCreated = (a, b) => (
  new Date(b.modified || b.created) - new Date(a.modified || a.created)
);

export function clientAnnotationToMercuryAnnotation({ target, body, ...rest }) {
  return {
    ...rest,
    target: createAnnotationTargetString(target),
    body: createAnnotationBodyString(body),
  };
}

export async function mercuryAnnotationToClientAnnotation(mercuryAnnotation) {
  const [{ id, type, subTarget }] = JSON.parse(mercuryAnnotation.target);
  const { ancestry } = await CoursewareTargetProvider.getCoursewareTypeAndAncestry(id, type);
  const [
    { elementId: currentElementId, elementType: currentElementType },
    { elementId: parentElementId, elementType: parentElementType },
  ] = ancestry;
  if (currentElementId) {
    await CoursewareTargetProvider.createCoursewareTarget(currentElementId, currentElementType);
  }
  if (parentElementId) {
    await CoursewareTargetProvider.createCoursewareTarget(parentElementId, parentElementType);
  }
  const targetEntity = await CoursewareTargetProvider.createCoursewareTarget(id, type);
  const target = {
    ...targetEntity,
    ancestry,
    subTarget,
  };
  const body = JSON.parse(mercuryAnnotation.body);
  return {
    ...mercuryAnnotation,
    body,
    target,
  };
}
